/**
 * Automatic Rules Settings Page
 * Displays and manages automatic send approvals for various action types
 * Shows ALL possible action types, with inactive ones displayed as disabled
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Card, Row, Col, Switch, Spin, Modal, Tag, Tooltip } from "antd";
import { ExclamationCircleOutlined, ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import { AutomaticRulesIcon } from "../assets/svg/AutomaticRulesIcon";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import moment from "moment";

import { WHITE, GRAY_500, GRAY_200, GRAY_300, PRIMARY_600 } from "../constant/colors";
import { SettingsIcon } from "../assets/svg/SettingsIcon";
import { selectUser, selectEnvironment } from "../store/selector";
import { useStandardizedTracking, EventNames } from "../utils/pendo/useStandardizedTracking";
import { getAllAutomaticApprovalsAPI, revokeAutomaticApprovalAPI, createAutomaticApprovalAPI } from "../api/api";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../component/customComponent/customMessages/SuccessMessage";
import { useGetSetting } from "../hooks/getSetting";

const { Text, Title } = Typography;

const AutomaticRulesContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
`;

const BreadcrumbSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: ${PRIMARY_600};
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${GRAY_500};
  font-size: 18px;
`;

const BreadcrumbCurrent = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: "Open Sans";
  font-size: 18px;
  font-weight: 600;
  color: #000000;
`;

const PageDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  display: block;
  margin-bottom: 24px;
`;

const ContentSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 4px;
`;

const RuleCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => (props.$isActive ? GRAY_200 : GRAY_300)};
  margin-bottom: 16px;
  opacity: ${(props) => (props.$isActive ? 1 : 0.7)};
  background: ${(props) => (props.$isActive ? WHITE : "#fafafa")};

  .ant-card-body {
    padding: 20px;
  }
`;

const RuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const RuleIcon = styled.div`
  width: 44px;
  height: 44px;
  background: ${(props) => (props.$isActive ? "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)" : "#f0f0f0")};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const RuleTitle = styled(Title)`
  margin: 0 !important;
  font-size: 16px !important;
  color: ${(props) => (props.$isActive ? "#000" : GRAY_500)} !important;
`;

const RuleDescription = styled(Text)`
  color: ${GRAY_500};
  font-size: 14px;
  display: block;
  margin-bottom: 16px;
`;

const RuleDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px;
  background: ${(props) => (props.$isActive ? "#f9fafb" : "#f5f5f5")};
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${GRAY_500};

  .detail-icon {
    color: ${PRIMARY_600};
  }

  .detail-value {
    color: #000;
    font-weight: 500;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const DisabledMessage = styled(Text)`
  color: ${GRAY_500};
  font-size: 13px;
  font-style: italic;
`;

const FEATURE_GROUP = "pending_actions";

const AutomaticRulesSettings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const { track } = useStandardizedTracking();

  // Check if pending actions feature and Express Enroll Invites sub-feature are enabled
  const [isPendingActionsEnabled, isExpressEnrollInvitesEnabled] = useGetSetting([
    "pending-actions.enabled",
    "pending-actions.express-enroll-invites.enabled"
  ]);

  // Define ALL possible action types with their display info
  // This list determines what shows up on the settings page
  const ALL_ACTION_TYPES = useMemo(
    () => [
      {
        key: "paytient",
        title: t("automatic_rules.action_types.paytient.title", "M3P Eligible Patients"),
        description: t(
          "automatic_rules.action_types.paytient.description",
          "Automatically send Medicare M3P enrollment invitations to eligible patients."
        ),
        icon: <AutomaticRulesIcon width={24} height={24} color="#1570EF" />
      },
      {
        key: "express_enroll_invites",
        title: t("automatic_rules.action_types.express_enroll_invites.title", "Express Enroll Eligible Patients"),
        description: t(
          "automatic_rules.action_types.express_enroll_invites.description",
          "Automatically send express enrollment invitations to eligible patients."
        ),
        icon: <AutomaticRulesIcon width={24} height={24} color={PRIMARY_600} />,
        enabledSetting: isExpressEnrollInvitesEnabled
      }
    ],
    [t, isExpressEnrollInvitesEnabled]
  );

  // Filter out action types whose feature flag is disabled
  const visibleActionTypes = useMemo(
    () => ALL_ACTION_TYPES.filter((action) => action.enabledSetting !== false),
    [ALL_ACTION_TYPES]
  );

  const [approvalsByType, setApprovalsByType] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState({});

  // Redirect to settings if feature is disabled
  useEffect(() => {
    if (isPendingActionsEnabled === false) {
      history.replace("/settings");
    }
  }, [isPendingActionsEnabled, history]);

  const fetchApprovals = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getAllAutomaticApprovalsAPI();
      if (response?.data?.success) {
        // Group approvals by action type, keeping only active ones
        const approvalMap = {};
        response.data.data.forEach((approval) => {
          if (approval.status === "active") {
            approvalMap[approval.actionType] = approval;
          }
        });
        setApprovalsByType(approvalMap);
      }
    } catch (error) {
      console.error("Error fetching automatic approvals:", error);
      ErrorMessage(t("automatic_rules.fetch_error", "Failed to load automatic rules"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchApprovals();
    track(EventNames.PAGE_IMPRESSION, {
      featureGroup: FEATURE_GROUP,
      pageKey: "automatic_rules_settings",
      contentKey: "automatic_rules_settings",
      additionalInfo: {
        username: user?.email,
        account
      }
    });
  }, [fetchApprovals, track, user?.email, account]);

  const handleToggleApproval = useCallback(
    async (actionTypeKey, isCurrentlyActive) => {
      const approval = approvalsByType[actionTypeKey];

      if (isCurrentlyActive && approval) {
        // Disable (revoke) the approval
        Modal.confirm({
          title: t("automatic_rules.revoke_confirm_title", "Disable Automatic Sending?"),
          content: t(
            "automatic_rules.revoke_confirm_description",
            "This will stop automatic sending for this action type. You can re-enable it anytime."
          ),
          icon: <ExclamationCircleOutlined />,
          okText: t("automatic_rules.revoke_confirm_ok", "Yes, Disable"),
          cancelText: t("automatic_rules.revoke_confirm_cancel", "Cancel"),
          okButtonProps: { danger: true },
          async onOk() {
            try {
              setIsToggling((prev) => ({ ...prev, [actionTypeKey]: true }));
              const response = await revokeAutomaticApprovalAPI(approval.id);
              if (response?.data?.success) {
                SuccessMessage(t("automatic_rules.revoke_success", "Automatic sending has been disabled"));
                fetchApprovals();
                track(EventNames.BUTTON_CLICK, {
                  featureGroup: FEATURE_GROUP,
                  pageKey: "automatic_rules_settings",
                  contentKey: "disable_automatic_rule",
                  additionalInfo: {
                    action_type: actionTypeKey,
                    username: user?.email,
                    account
                  }
                });
              } else {
                ErrorMessage(t("automatic_rules.revoke_error", "Failed to disable automatic sending"));
              }
            } catch (error) {
              console.error("Error revoking approval:", error);
              ErrorMessage(t("automatic_rules.revoke_error", "Failed to disable automatic sending"));
            } finally {
              setIsToggling((prev) => ({ ...prev, [actionTypeKey]: false }));
            }
          }
        });
      } else {
        // Enable (create) the approval
        try {
          setIsToggling((prev) => ({ ...prev, [actionTypeKey]: true }));
          const response = await createAutomaticApprovalAPI(actionTypeKey, {});
          if (response?.data?.success) {
            SuccessMessage(t("automatic_rules.enable_success", "Automatic sending has been enabled"));
            fetchApprovals();
            track(EventNames.BUTTON_CLICK, {
              featureGroup: FEATURE_GROUP,
              pageKey: "automatic_rules_settings",
              contentKey: "enable_automatic_rule",
              additionalInfo: {
                action_type: actionTypeKey,
                username: user?.email,
                account
              }
            });
          } else {
            ErrorMessage(t("automatic_rules.enable_error", "Failed to enable automatic sending"));
          }
        } catch (error) {
          console.error("Error enabling approval:", error);
          ErrorMessage(t("automatic_rules.enable_error", "Failed to enable automatic sending"));
        } finally {
          setIsToggling((prev) => ({ ...prev, [actionTypeKey]: false }));
        }
      }
    },
    [approvalsByType, t, fetchApprovals, track, user?.email, account]
  );

  const renderRuleCard = (actionType) => {
    const approval = approvalsByType[actionType.key];
    const isActive = !!approval;
    const iconColor = isActive ? PRIMARY_600 : GRAY_500;

    return (
      <RuleCard key={actionType.key} $isActive={isActive}>
        <RuleHeader>
          <Row align="middle">
            <Col>
              <RuleIcon $isActive={isActive}>
                {React.cloneElement(actionType.icon, { style: { fontSize: 24, color: iconColor } })}
              </RuleIcon>
            </Col>
            <Col>
              <RuleTitle level={5} $isActive={isActive}>
                {actionType.title}
              </RuleTitle>
              <Tag color={isActive ? "green" : "default"} style={{ marginTop: 4 }}>
                {isActive
                  ? t("automatic_rules.status_active", "Active")
                  : t("automatic_rules.status_inactive", "Inactive")}
              </Tag>
            </Col>
          </Row>
          <Tooltip
            title={
              isActive
                ? t("automatic_rules.toggle_disable", "Click to disable automatic sending")
                : t("automatic_rules.toggle_enable", "Click to enable automatic sending")
            }
          >
            <Switch
              checked={isActive}
              loading={isToggling[actionType.key]}
              onChange={() => handleToggleApproval(actionType.key, isActive)}
            />
          </Tooltip>
        </RuleHeader>

        <RuleDescription>{actionType.description}</RuleDescription>

        {isActive && approval ? (
          <RuleDetails $isActive={true}>
            <DetailItem>
              <UserOutlined className="detail-icon" />
              <span>{t("automatic_rules.enabled_by", "Enabled by:")}</span>
              <span className="detail-value">{approval.approverName || approval.approvedBy}</span>
            </DetailItem>
            <DetailItem>
              <ClockCircleOutlined className="detail-icon" />
              <span>{t("automatic_rules.enabled_on", "Enabled on:")}</span>
              <span className="detail-value">
                {approval.approvedAt ? moment(approval.approvedAt).format("MMM D, YYYY h:mm A") : "N/A"}
              </span>
            </DetailItem>
          </RuleDetails>
        ) : (
          <RuleDetails $isActive={false}>
            <DisabledMessage>
              {t(
                "automatic_rules.not_enabled_message",
                "Automatic sending is not enabled. Toggle the switch to enable."
              )}
            </DisabledMessage>
          </RuleDetails>
        )}
      </RuleCard>
    );
  };

  return (
    <AutomaticRulesContainer>
      <HeaderSection>
        <BreadcrumbSection>
          <BreadcrumbItem onClick={() => history.push("/settings")}>
            <SettingsIcon />
            Settings
          </BreadcrumbItem>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbCurrent>
            <AutomaticRulesIcon width={18} height={18} />
            {t("automatic_rules.page_title", "Automatic Rules")}
          </BreadcrumbCurrent>
        </BreadcrumbSection>
        <PageDescription>
          {t(
            "automatic_rules.page_description",
            "Manage automatic sending rules. When enabled, the system will automatically send notifications to eligible patients based on configured schedules."
          )}
        </PageDescription>
      </HeaderSection>

      <ContentSection>
        {isLoading ? (
          <LoadingContainer>
            <Spin size="large" />
          </LoadingContainer>
        ) : (
          visibleActionTypes.map(renderRuleCard)
        )}
      </ContentSection>
    </AutomaticRulesContainer>
  );
};

export default AutomaticRulesSettings;
